import { useMemo } from 'react'

import { capitalize, get } from 'lodash-es'

import {
  DataFrame,
  dataToDataFrame,
  exampleToRow,
  ExampleColumn,
  Item,
  exampleColumnMappings,
  providerColumnMap,
} from './lib'

import { ExampleEntityType, MetricExample, MetricMetadataType, ReportProvider } from '@percept/types'


export type UseDataFrameProps = {
  provider?: ReportProvider
  metadata?: MetricMetadataType
  activePerformanceTail?: string | null
  examples: MetricExample[]
  currency: string | null | undefined
}

// NOTE - some entity examples currently include no performance values,
// so we model these in order to remove perf columns from the base column list
// to avoid showing misleading 0s or N/A values.
const entitiesWithoutPerformance: ExampleEntityType[] = [
  'AssetGroupAsset',
  'CampaignAssetGroup',
]


export const useDataFrame = ({
  provider,
  activePerformanceTail,
  metadata,
  examples,
  currency,
}: UseDataFrameProps): DataFrame => {

  return useMemo(() => {
    const rows = examples.map( example => (
      exampleToRow(
        example as Item,
        {
          activePerformanceTail,
          extraAttributes: { currency },
        })
    ))

    const baseColumns = provider && providerColumnMap[provider] || []

    const additionalColumns = [
      ...Object.keys(get(examples[0], 'attributes') || {}),
    ].filter( attr => !!(metadata && metadata[attr]) )

    const columns = [
      ...baseColumns,
      ...additionalColumns
    ].map( key => {
      return {
        key,
        text: (
          metadata && metadata[key] && metadata[key].title ||
          exampleColumnMappings[key as ExampleColumn] ||
          capitalize(key)
        ), 
      }
    })

    const entityType = examples[0] && examples[0].type || null
    const disablePerformanceColumns = entitiesWithoutPerformance.includes(entityType)

    return dataToDataFrame({ rows, columns, disablePerformanceColumns })
  }, [provider, metadata, examples, currency, activePerformanceTail])

}
